import React from "react";

import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";

import VisibilityIcon from "@mui/icons-material/Visibility";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";

import { displayList } from "@auto/widgets/ListComponent";
import { getLocale, rest, t } from "@auto/services";
import { getServiceUri } from "@auto/metadata";
import { can } from "@src/security/permissions";

import {
  VOTER_APPLICATION_LIST_FACETS,
  buildVoterApplicationListPayload,
} from "./VoterApplicationListFacets";

import styles from "./VoterApplicationList.module.styl";

import InlineTags from "@auto/widgets/InlineTags";

const RELATED_FETCH_LIMIT = 5000;
const DEFAULT_PAGE_SIZE = 50;

let initialRange = null;
let initialRangeUsesLeft = 0;

const getField = (row, camelName, snakeName) => {
  return row?.[camelName] ?? row?.[snakeName] ?? "";
};

const formatTagDateTime = (value) => {
  if (!value) {
    return "";
  }

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return String(value);
  }

  try {
    return new Intl.DateTimeFormat(getLocale(), {
      dateStyle: "short",
      timeStyle: "medium",
    }).format(date);
  } catch {
    return String(value);
  }
};

const flattenTagValue = (value) => {
  if (value === true || value === null || value === undefined || value === "") {
    return value;
  }

  if (Array.isArray(value)) {
    return value.map(flattenTagValue).filter(Boolean).join(", ");
  }

  if (typeof value === "object") {
    return Object.entries(value)
      .map(([key, nestedValue]) => `${key}: ${flattenTagValue(nestedValue)}`)
      .join(" · ");
  }

  return value;
};

const formatTagValue = (key, value) => {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    return flattenTagValue(value);
  }

  const normalizedKey = String(key || "").trim().toUpperCase();

  if (normalizedKey === "ONI_ERROR") {
    const parts = [];
    const count = Number(value.count);

    if (Number.isFinite(count)) {
      parts.push(`${t("voter.attempts")}: ${count}`);
    }

    if (value.lastAttempt) {
      parts.push(`${t("voter.lastAttempt")}: ${formatTagDateTime(value.lastAttempt)}`);
    }

    return parts.length ? parts.join(" · ") : flattenTagValue(value);
  }

  if (normalizedKey === "DUPLICATE") {
    const parts = [];

    if (value.primaryApplicationId !== null && value.primaryApplicationId !== undefined) {
      parts.push(`${t("voter.primaryApplication")}: ${value.primaryApplicationId}`);
    }

    if (value.rejectedAt) {
      parts.push(`${t("voter.rejectedAt")}: ${formatTagDateTime(value.rejectedAt)}`);
    }

    return parts.length ? parts.join(" · ") : flattenTagValue(value);
  }

  if (normalizedKey === "RETRANSMISSION" && value.archivedAt) {
    return `${t("voter.archivedAt")}: ${formatTagDateTime(value.archivedAt)}`;
  }

  return flattenTagValue(value);
};

const renderTags = (row) => {
  const tags =
    row?.tags && typeof row.tags === "object" && !Array.isArray(row.tags)
      ? row.tags
      : {};

  if (!Object.keys(tags).length) {
    return null;
  }

  const displayTags = Object.fromEntries(
    Object.entries(tags).map(([key, value]) => [key, formatTagValue(key, value)])
  );

  return (
    <InlineTags
      tags={displayTags}
      disabled
      showHeader={false}
      emptyText=""
    />
  );
};

const uniqNonNull = (values) => {
  const unique = new Set();

  for (const value of values || []) {
    if (value !== null && value !== undefined && value !== "") {
      unique.add(value);
    }
  }

  return Array.from(unique);
};

const quoteRsqlValue = (value) => {
  if (typeof value === "number") {
    return String(value);
  }

  return `"${String(value).replaceAll('"', '\\"')}"`;
};

const rsqlIn = (selector, values) => {
  return `${selector}=in=(${values.map(quoteRsqlValue).join(",")})`;
};

const indexByGid = (rows) => {
  const byGid = new Map();

  for (const row of rows || []) {
    if (row.gid !== null && row.gid !== undefined) {
      byGid.set(row.gid, row);
    }
  }

  return byGid;
};

const formatStatusCode = (value) => {
  const normalized =
    value === null || value === undefined
      ? ""
      : String(value).trim();

  switch (normalized) {
    case "1":
      return t("common.submitted");

    case "2":
      return t("common.error");

    case "3":
      return t("common.approved");

    case "4":
      return t("voter.manualAdjudication");

    case "5":
      return t("voter.retransmission");

    case "6":
      return t("voter.duplicate");

    case "7":
      return t("common.rejected");

    case "8":
      return t("common.archived");

    case "9":
      return t("voter.checked");

    default:
      return normalized;
  }
};

const formatSource = (value) => {
  const normalized =
    value === null || value === undefined
      ? ""
      : String(value).trim();

  switch (normalized) {
    case "1":
      return t("voter.oniSearch");

    case "2":
      return t("voter.mrzNfc");

    case "3":
      return t("voter.manualEntry");

    default:
      return normalized;
  }
};

const formatApplicationType = (value) => {
  const normalized =
    value === null || value === undefined || value === ""
      ? "1"
      : String(value).trim();

  switch (normalized) {
    case "1":
      return t("voter.standard");

    case "2":
      return t("voter.correction");

    default:
      return normalized;
  }
};

const buildApplicantName = (application) => {
  const firstName = String(getField(application, "firstName", "first_name")).trim();
  const lastName = String(getField(application, "lastName", "last_name")).trim();

  return [firstName, lastName].filter(Boolean).join(" ");
};

const pickVotingCenterLabel = (votingCenter) => {
  if (!votingCenter) {
    return "";
  }

  return (
    votingCenter.name ||
    votingCenter.code ||
    (votingCenter.gid !== null && votingCenter.gid !== undefined
      ? String(votingCenter.gid)
      : "")
  );
};

const voterApplicationPortraitSrc = (row) => {
  const id = getField(row, "id", "id");

  if (!id) {
    return "";
  }

  return `${getServiceUri()}voter-application/portrait/${id}`;
};

const oniPortraitSrc = (row) => {
  const id = getField(row, "oniPhotoId", "oni_photo_id");

  if (!id) {
    return "";
  }

  return `${getServiceUri()}person-photo/data/${id}`;
};

const renderOniPortrait = (row, gui) => {
  const src = can(gui, "READ_PERSON_PHOTO") ? oniPortraitSrc(row) : "";

  if (!src) {
    return (
      <AccountCircleIcon
        className={styles.avatarPlaceholder}
        sx={{ width: 48, height: 48 }}
      />
    );
  }

  return (
    <img
      src={src}
      alt={row.applicantName || t("voter.oniPhotoAlt")}
      className={styles.avatar}
      onError={(event) => {
        event.currentTarget.style.display = "none";
      }}
    />
  );
};

const renderPortrait = (row, gui) => {
  const src = can(gui, "READ_VOTER_APPLICATION") ? voterApplicationPortraitSrc(row) : "";

  if (!src) {
    return <AccountCircleIcon className={styles.avatarPlaceholder} />;
  }

  return (
    <img
      src={src}
      alt={row.applicantName || t("voter.applicationPhotoAlt")}
      className={styles.avatar}
      onError={(event) => {
        event.currentTarget.style.display = "none";
      }}
    />
  );
};

const loadRelatedRows = async (applications) => {
  const votingCenterGids = uniqNonNull(
    (applications || []).map((application) =>
      getField(application, "votingCenterGid", "voting_center_gid")
    )
  );

  const votingCenters = votingCenterGids.length
    ? await rest.request(getServiceUri() + "voting-center/search", "POST", {
        columns: [],
        query: rsqlIn("gid", votingCenterGids),
        limit: RELATED_FETCH_LIMIT,
        offset: 0,
      })
    : [];

  return {
    votingCenterByGid: indexByGid(votingCenters),
  };
};

const enrichVoterApplicationRows = async (applications) => {
  const { votingCenterByGid } = await loadRelatedRows(applications);

  return (applications || []).map((application) => {
    const votingCenterGid = getField(
      application,
      "votingCenterGid",
      "voting_center_gid"
    );

    const votingCenter = votingCenterByGid.get(votingCenterGid);

    return {
      ...application,

      id: getField(application, "id", "id"),
      gid: getField(application, "gid", "gid"),

      votingCenterGid,

      applicantName: buildApplicantName(application),
      applicantNationalIdentifier: getField(
        application,
        "nationalIdentifier",
        "national_identifier"
      ),
      applicantBirthDate: getField(application, "birthDate", "birth_date"),

      votingCenterLabel: pickVotingCenterLabel(votingCenter),

      operatorUserGid: getField(
        application,
        "operatorUserGid",
        "operator_user_gid"
      ),

      receiptNumber: getField(application, "receiptNumber", "receipt_number"),

      sourceFormatted: formatSource(getField(application, "source", "source")),

      applicationTypeFormatted: formatApplicationType(
        getField(application, "applicationType", "application_type")
      ),

      statusCodeFormatted: formatStatusCode(
        getField(application, "statusCode", "status_code")
      ),
    };
  });
};

const readRangeFromUrl = () => {
  const params = new URLSearchParams(window.location.search);
  const offset = Number(params.get("offset"));
  const limit = Number(params.get("limit"));

  const safeLimit = Number.isFinite(limit) && limit > 0
    ? Math.floor(limit)
    : DEFAULT_PAGE_SIZE;

  const safeOffset = Number.isFinite(offset) && offset >= 0
    ? Math.floor(offset)
    : 0;

  return {
    offset: safeOffset,
    limit: safeLimit,
    page: Math.floor(safeOffset / safeLimit),
    pageSize: safeLimit,
    hasExplicitRange: params.has("offset") || params.has("limit"),
  };
};

const writeRangeToUrl = ({ offset, limit }) => {
  const url = new URL(window.location.href);
  url.searchParams.set("offset", String(offset));
  url.searchParams.set("limit", String(limit));
  window.history.replaceState({}, "", url.toString());
};

const getEffectiveQuery = (query) => {
  const search = String(query?.search || "").trim();
  const filters = query?.filters || {};
  const sort = Array.isArray(query?.sort) ? query.sort : [];

  const page = Number.isFinite(query?.page) ? query.page : 0;
  const pageSize = Number.isFinite(query?.pageSize) ? query.pageSize : DEFAULT_PAGE_SIZE;

  if (
    initialRange &&
    initialRange.hasExplicitRange &&
    initialRangeUsesLeft > 0 &&
    !search &&
    page === 0 &&
    pageSize === DEFAULT_PAGE_SIZE
  ) {
    initialRangeUsesLeft -= 1;

    return {
      page: initialRange.page,
      pageSize: initialRange.pageSize,
      offset: initialRange.offset,
      limit: initialRange.limit,
      search,
      filters,
      sort,
    };
  }

  initialRangeUsesLeft = 0;

  return {
    page,
    pageSize,
    offset: page * pageSize,
    limit: pageSize,
    search,
    filters,
    sort,
  };
};

const voterApplicationListProjection = {
  storageKey: "voter-application",
  facets: VOTER_APPLICATION_LIST_FACETS,

  columns: [
      {
      title: "voter.oniPhoto",
      field: "oniPhotoId",
      sorting: false,
      filtering: false,
      render: renderOniPortrait,
    },

    {
      title: "voter.applicationPhoto",
      field: "id",
      sorting: false,
      filtering: false,
      render: renderPortrait,
    },

    {
      title: "person.nationalId",
      field: "applicantNationalIdentifier",
      render: (row) => row.applicantNationalIdentifier || null,
    },

    {
      title: "voter.applicant",
      field: "applicantName",
      render: (row) => row.applicantName || null,
    },

    {
      title: "voter.birthDate",
      field: "applicantBirthDate",
      render: (row) => row.applicantBirthDate || null,
    },

    {
      title: "voter.votingCenter",
      field: "votingCenterLabel",
      sorting: false,
      filtering: false,
      render: (row) => row.votingCenterLabel || null,
    },

    {
      title: "voter.receiptNumber",
      field: "receiptNumber",
      render: (row) => row.receiptNumber || null,
    },

    {
      title: "common.source",
      field: "sourceFormatted",
      render: (row) => row.sourceFormatted || null,
    },

    {
      title: "voter.applicationType",
      field: "applicationTypeFormatted",
      render: (row) => row.applicationTypeFormatted || null,
    },

    {
      title: "common.status",
      field: "statusCodeFormatted",
      render: (row) => row.statusCodeFormatted || null,
    },

    {
      title: "common.tags",
      field: "tags",
      sorting: false,
      filtering: false,
      render: renderTags,
    },

    {
      title: "common.actions",
      field: "__actions",
      sorting: false,
      filtering: false,
    },
  ],

  actions: (row, gui) => (
    can(gui, "READ_VOTER_APPLICATION") ? (
      <Tooltip title={t("voter.viewApplication")}>
        <IconButton
          size="small"
          onClick={(event) => {
            event.preventDefault();
            event.stopPropagation();

            if (row.gid) {
              gui.goTo(`/voter-application/${row.gid}`);
            }
          }}
        >
          <VisibilityIcon fontSize="small" />
        </IconButton>
      </Tooltip>
    ) : null
  ),

  fetch: async (query) => {
    const range = getEffectiveQuery(query);

    writeRangeToUrl({
      offset: range.offset,
      limit: range.limit,
    });

    const payload = buildVoterApplicationListPayload(range);

    const applications = await rest.request(
      getServiceUri() + "voter-application-list/search",
      "POST",
      payload
    );

    const totalCount = await rest.request(
      getServiceUri() + "voter-application-list/count",
      "POST",
      payload
    );

    return {
      data: await enrichVoterApplicationRows(applications || []),
      totalCount,
      page: range.page,
    };
  },
};

export const displayVoterApplicationList = (gui) => {
  initialRange = readRangeFromUrl();
  initialRangeUsesLeft = initialRange.hasExplicitRange ? 3 : 0;

  return displayList(gui, voterApplicationListProjection);
};

export default displayVoterApplicationList;
